import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const EventsContext = createContext();

// Only repeat-poll on public browsing pages
function shouldRepeatPoll(pathname) {
  if (pathname.startsWith('/panel')) return false;
  if (pathname.startsWith('/event/')) return false;
  return true;
}

export function EventsProvider({ children }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const location = useLocation();
  const repeatPoll = shouldRepeatPoll(location.pathname);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch('/api/events');
        if (!response.ok) {
          throw new Error('Failed to fetch events');
        }
        const data = await response.json();
        setEvents(data.events || []);
        setLastUpdated(data.lastUpdated);
        setError(null);
      } catch (err) {
        console.error('Error fetching events:', err);
        setError(err.message);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
    if (!repeatPoll) return;
    const interval = setInterval(fetchEvents, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [repeatPoll]);

  return (
    <EventsContext.Provider value={{ events, loading, error, lastUpdated }}>
      {children}
    </EventsContext.Provider>
  );
}

export function useEvents() {
  const context = useContext(EventsContext);
  if (!context) {
    throw new Error('useEvents must be used within an EventsProvider');
  }
  return context;
}
